/**
 * `health`, `documentTasks` and `nudges` as fixture data — the counterpart to `fakeAutomation.ts`
 * for the two top-level namespaces that sit beside `automation` rather than inside it.
 *
 * Gating mirrors the server exactly where it matters: `billing.usage` and
 * `notification-deliveries.list` are workspace-Admin-only (F-4 in the wave record — the real
 * server answers a BARE 403 for the usage call), and `saveSettings` merges rather than
 * replaces, proving F-1 cannot regress even though FakeApi never round-trips through the wire.
 */
import type { DeliveryQuery } from "~/api/client";
import { ForbiddenError, NotFoundError } from "~/api/errors";
import type {
  BillingUsage,
  DeliveryPage,
  DeliveryRow,
  DocumentTask,
  DocumentTaskInput,
  DocumentTaskPatch,
  FlowHealthSettings,
  FlowHealthSettingsPatch,
  Nudge,
  NudgeTarget,
  QuickWhenResult,
} from "~/api/opsTypes";
import { ABILITY, type Abilities } from "~/api/types";

const BASE_TIME = Date.parse("2026-08-12T09:00:00.000Z");
const hoursAgo = (h: number) => new Date(BASE_TIME - h * 3600_000).toISOString();
const daysAgo = (d: number) => new Date(BASE_TIME - d * 86_400_000).toISOString().slice(0, 10);

/** 14 daily rows, 3 recent runs (one failed, one with a null status → "purged"). */
const BILLING_SEED = {
  daily: Array.from({ length: 14 }, (_, index) => ({
    date: daysAgo(13 - index),
    tokensIn: 40_000 + index * 3_000,
    tokensOut: 12_000 + index * 900,
  })),
  recentRuns: [
    {
      runId: "run-live",
      name: "Morning brief",
      status: "running",
      tokensIn: 5_400_000,
      tokensOut: 91_300_000,
      source: "run:ide",
      occurredAt: hoursAgo(0.2),
    },
    {
      runId: "run-failed",
      name: "Release notes from the changelog",
      status: "failed",
      tokensIn: 2_100,
      tokensOut: 640,
      source: "server",
      occurredAt: hoursAgo(50),
    },
    {
      // Billing rows outlive run retention by design — a purged run still has a charge.
      runId: "run-purged-1",
      name: null,
      status: null,
      tokensIn: 8_200,
      tokensOut: 1_900,
      source: "server",
      occurredAt: hoursAgo(400),
    },
  ],
};

const DELIVERY_CHANNELS = ["app", "email", "chat", "webhook"] as const;
const DELIVERY_STATUSES = ["sent", "sent", "sent", "failed", "skipped"] as const;
const DELIVERY_EVENTS = [
  "documents.publish",
  "documents.mention",
  "runs.failed",
  "runs.approval_required",
  "comments.reply",
];

/** ~30 rows across sent/failed/skipped × app/email — enough to need the pager (page size 25). */
function seedDeliveries(): DeliveryRow[] {
  return Array.from({ length: 32 }, (_, index) => {
    const channel = DELIVERY_CHANNELS[index % DELIVERY_CHANNELS.length] ?? "app";
    const status = DELIVERY_STATUSES[index % DELIVERY_STATUSES.length] ?? "sent";
    return {
      id: `delivery-${index}`,
      notificationId: `notification-${Math.floor(index / 2)}`,
      event: DELIVERY_EVENTS[index % DELIVERY_EVENTS.length] ?? "documents.publish",
      userId: index % 3 === 0 ? "user-2" : "user-1",
      userName: index % 3 === 0 ? "Grace Hopper" : "Ada Lovelace",
      userEmail: index % 3 === 0 ? "grace@example.com" : "ada@example.com",
      channel,
      status,
      attemptedAt: hoursAgo(index * 1.5),
      error: status === "failed" ? "SMTP timeout after 8s" : null,
      detail: status === "skipped" ? "channel disabled by recipient preference" : null,
    };
  });
}

const DOCUMENT_TASK_SEEDS: DocumentTask[] = [
  {
    id: "doctask-1",
    documentId: "doc-1",
    title: "Confirm the Q3 numbers with finance",
    details: null,
    status: "open",
    assigneeId: "user-1",
    dueDate: new Date(BASE_TIME - 2 * 86_400_000).toISOString(),
    sourceQuote: "revenue grew 12% quarter over quarter",
    relatedDocumentIds: ["doc-2"],
    createdById: "user-2",
    createdAt: hoursAgo(72),
    updatedAt: hoursAgo(72),
    completedAt: null,
  },
  {
    id: "doctask-2",
    documentId: "doc-1",
    title: "Add the migration rollback plan",
    details: "Needs a runbook link before this ships.",
    status: "open",
    assigneeId: null,
    dueDate: new Date(BASE_TIME + 3 * 86_400_000).toISOString(),
    sourceQuote: null,
    relatedDocumentIds: [],
    createdById: "user-1",
    createdAt: hoursAgo(20),
    updatedAt: hoursAgo(20),
    completedAt: null,
  },
  {
    id: "doctask-3",
    documentId: "doc-1",
    title: "Circulate for legal review",
    details: null,
    status: "open",
    assigneeId: "user-2",
    dueDate: null,
    sourceQuote: "this clause needs sign-off",
    relatedDocumentIds: ["doc-2", "doc-3"],
    createdById: "user-1",
    createdAt: hoursAgo(5),
    updatedAt: hoursAgo(5),
    completedAt: null,
  },
];

const NUDGE_SEEDS: Nudge[] = [
  {
    id: "nudge-1",
    targetType: "document",
    documentId: "doc-1",
    partId: null,
    recordId: null,
    whenUtc: new Date(BASE_TIME + 3 * 3600_000).toISOString(),
    timezone: "UTC",
    recurrenceRule: null,
    note: "Follow up on the finance thread",
    channels: { inApp: true, email: false },
    status: "scheduled",
    firedAtUtc: null,
    createdAt: hoursAgo(4),
  },
  {
    id: "nudge-2",
    targetType: "task",
    documentId: null,
    partId: null,
    recordId: "doctask-2",
    whenUtc: new Date(BASE_TIME + 26 * 3600_000).toISOString(),
    timezone: "UTC",
    recurrenceRule: "FREQ=WEEKLY",
    note: null,
    channels: { inApp: true, email: true },
    status: "scheduled",
    firedAtUtc: null,
    createdAt: hoursAgo(10),
  },
  {
    id: "nudge-3",
    targetType: "document",
    documentId: "doc-2",
    partId: null,
    recordId: null,
    whenUtc: new Date(BASE_TIME + 8 * 3600_000).toISOString(),
    timezone: "UTC",
    recurrenceRule: null,
    note: "Snoozed from yesterday",
    channels: { inApp: true, email: false },
    status: "snoozed",
    firedAtUtc: null,
    createdAt: hoursAgo(30),
  },
];

export interface FakeOpsDeps {
  requireSession(): unknown;
  abilities(): Abilities;
  delay<T>(value: T): Promise<T>;
}

export function createFakeOps(deps: FakeOpsDeps) {
  // The merge test's proof surface: FlowHealthSettings only models three keys, but the store
  // itself is not wiped by a partial save — the same guarantee the real adapter's
  // read-modify-write gives against `teams.update`'s whole-object replace (F-1).
  let settings: FlowHealthSettings = {
    flowsNotifyOnFailure: true,
    flowsBillingEnabled: true,
    flowsAuditRetentionDays: null,
  };

  let documentTasks: DocumentTask[] = DOCUMENT_TASK_SEEDS.map((task) => ({ ...task }));
  let nudges: Nudge[] = NUDGE_SEEDS.map((nudge) => ({ ...nudge }));
  const deliveries = seedDeliveries();

  const requireAdmin = (message: string): void => {
    if (deps.abilities()[ABILITY.workspaceUpdate] !== true) {
      throw new ForbiddenError(message);
    }
  };

  const findDocumentTask = (id: string): DocumentTask => {
    const found = documentTasks.find((task) => task.id === id);
    if (!found) {
      throw new NotFoundError("Document task not found");
    }
    return found;
  };

  const findNudge = (id: string): Nudge => {
    const found = nudges.find((nudge) => nudge.id === id);
    if (!found) {
      throw new NotFoundError("Nudge not found");
    }
    return found;
  };

  return {
    health: {
      usage: async (days = 30): Promise<BillingUsage> => {
        deps.requireSession();
        // The real server's `billing.usage` returns a BARE 403 (empty body) for a non-Admin —
        // the message here is what the scene is told to show regardless (F-10 in the wave record).
        requireAdmin("Usage could not be loaded — this view is admin-only.");
        const tokensIn = BILLING_SEED.daily.reduce((sum, day) => sum + day.tokensIn, 0);
        const tokensOut = BILLING_SEED.daily.reduce((sum, day) => sum + day.tokensOut, 0);
        return deps.delay({
          windowDays: days,
          ai: { tokensIn, tokensOut, total: tokensIn + tokensOut },
          // Storage meters are LEVELS, never sums — the latest snapshot, not an accumulation.
          storage: { artifactBytes: 412_000_000, attachmentBytes: 88_000_000 },
          daily: BILLING_SEED.daily.map((day) => ({ ...day })),
          recentRuns: BILLING_SEED.recentRuns.map((run) => ({ ...run })),
        });
      },

      settings: async (): Promise<FlowHealthSettings> => {
        deps.requireSession();
        return deps.delay({ ...settings });
      },

      saveSettings: async (patch: FlowHealthSettingsPatch): Promise<FlowHealthSettings> => {
        deps.requireSession();
        requireAdmin("You cannot change flow health settings in this workspace");
        if (
          patch.flowsAuditRetentionDays !== undefined &&
          patch.flowsAuditRetentionDays !== null &&
          (patch.flowsAuditRetentionDays < 7 || patch.flowsAuditRetentionDays > 3650)
        ) {
          throw new ForbiddenError("Retention must be between 7 and 3650 days");
        }
        settings = { ...settings, ...patch };
        return deps.delay({ ...settings });
      },

      deliveries: async (query: DeliveryQuery = {}): Promise<DeliveryPage> => {
        deps.requireSession();
        requireAdmin("You do not have permission to read delivery history in this workspace");
        const filtered = deliveries
          .filter((row) => (query.channel ? row.channel === query.channel : true))
          .filter((row) => (query.status ? row.status === query.status : true));
        const offset = query.offset ?? 0;
        const limit = query.limit ?? 25;
        return deps.delay({
          offset,
          limit,
          total: filtered.length,
          data: filtered.slice(offset, offset + limit).map((row) => ({ ...row })),
        });
      },
    },

    documentTasks: {
      list: async (
        query: { documentId?: string; assignedToMe?: boolean; includeDone?: boolean } = {},
      ): Promise<DocumentTask[]> => {
        deps.requireSession();
        return deps.delay(
          documentTasks
            .filter((task) => (query.documentId ? task.documentId === query.documentId : true))
            .filter((task) => (query.includeDone ? true : task.status !== "done"))
            .map((task) => ({ ...task })),
        );
      },

      create: async (input: DocumentTaskInput): Promise<DocumentTask> => {
        deps.requireSession();
        if (!input.title.trim()) {
          throw new ForbiddenError("title_required");
        }
        const task: DocumentTask = {
          id: `doctask-${documentTasks.length + 1}`,
          documentId: input.documentId,
          title: input.title,
          details: input.details ?? null,
          status: "open",
          assigneeId: input.assigneeId ?? null,
          dueDate: input.dueDate ?? null,
          sourceQuote: input.sourceQuote ?? null,
          relatedDocumentIds: input.relatedDocumentIds ?? [],
          createdById: "user-1",
          createdAt: new Date(BASE_TIME).toISOString(),
          updatedAt: new Date(BASE_TIME).toISOString(),
          completedAt: null,
        };
        documentTasks = [...documentTasks, task];
        return deps.delay({ ...task });
      },

      update: async (id: string, patch: DocumentTaskPatch): Promise<DocumentTask> => {
        deps.requireSession();
        const found = findDocumentTask(id);
        const updated: DocumentTask = {
          ...found,
          ...(patch.title !== undefined ? { title: patch.title } : {}),
          ...(patch.details !== undefined ? { details: patch.details } : {}),
          // hasAssignee/hasDueDate are the tri-state discriminators — without them "unchanged"
          // and "cleared" cannot be told apart. FakeApi enforces the same contract.
          ...(patch.hasAssignee ? { assigneeId: patch.assigneeId ?? null } : {}),
          ...(patch.hasDueDate ? { dueDate: patch.dueDate ?? null } : {}),
          ...(patch.relatedDocumentIds !== undefined
            ? { relatedDocumentIds: patch.relatedDocumentIds }
            : {}),
          updatedAt: new Date(BASE_TIME).toISOString(),
        };
        documentTasks = documentTasks.map((task) => (task.id === id ? updated : task));
        return deps.delay({ ...updated });
      },

      complete: async (id: string, done: boolean): Promise<DocumentTask> => {
        deps.requireSession();
        const found = findDocumentTask(id);
        const updated: DocumentTask = {
          ...found,
          status: done ? "done" : "open",
          completedAt: done ? new Date(BASE_TIME).toISOString() : null,
          updatedAt: new Date(BASE_TIME).toISOString(),
        };
        documentTasks = documentTasks.map((task) => (task.id === id ? updated : task));
        return deps.delay({ ...updated });
      },

      remove: async (id: string): Promise<void> => {
        deps.requireSession();
        findDocumentTask(id);
        documentTasks = documentTasks.filter((task) => task.id !== id);
        return deps.delay(undefined);
      },
    },

    nudges: {
      list: async (documentId?: string, includeResolved = false): Promise<Nudge[]> => {
        deps.requireSession();
        const resolved = new Set(["done", "cancelled"]);
        return deps.delay(
          nudges
            .filter((nudge) => (documentId ? nudge.documentId === documentId : true))
            .filter((nudge) => (includeResolved ? true : !resolved.has(nudge.status)))
            .map((nudge) => ({ ...nudge })),
        );
      },

      create: async (target: NudgeTarget, when: QuickWhenResult, email = false): Promise<Nudge> => {
        deps.requireSession();
        if (!when.whenUtc) {
          throw new ForbiddenError("when_required");
        }
        // Email is opt-in ESTATE-WIDE. `NudgeJob` honours a true value, so the client's `false`
        // is the only thing between a reminder and somebody's mailbox — which makes it a rule
        // worth failing a test over, not a convention worth remembering (W7 §A3.3 / §E2 R9).
        if (email) {
          throw new ForbiddenError(
            "Email delivery is opt-in — this workspace has not turned it on",
          );
        }
        const nudge: Nudge = {
          id: `nudge-${nudges.length + 1}`,
          targetType: target.targetType,
          documentId: target.documentId ?? null,
          partId: target.partId ?? null,
          recordId: target.recordId ?? null,
          whenUtc: when.whenUtc,
          timezone: when.timezone,
          recurrenceRule: when.recurrenceRule ?? null,
          note: when.note ?? null,
          // Email is opt-in, estate-wide — never defaulted to true.
          channels: { inApp: true, email },
          status: "scheduled",
          firedAtUtc: null,
          createdAt: new Date(BASE_TIME).toISOString(),
        };
        nudges = [...nudges, nudge];
        return deps.delay({ ...nudge });
      },

      snooze: async (id: string, whenUtc: string): Promise<Nudge> => {
        deps.requireSession();
        const found = findNudge(id);
        if (!whenUtc) {
          throw new ForbiddenError("when_required");
        }
        const updated: Nudge = { ...found, whenUtc, status: "snoozed" };
        nudges = nudges.map((nudge) => (nudge.id === id ? updated : nudge));
        return deps.delay({ ...updated });
      },

      markDone: async (id: string): Promise<Nudge> => {
        deps.requireSession();
        const found = findNudge(id);
        const updated: Nudge = { ...found, status: "done" };
        nudges = nudges.map((nudge) => (nudge.id === id ? updated : nudge));
        return deps.delay({ ...updated });
      },

      cancel: async (id: string): Promise<Nudge> => {
        deps.requireSession();
        const found = findNudge(id);
        const updated: Nudge = { ...found, status: "cancelled" };
        nudges = nudges.map((nudge) => (nudge.id === id ? updated : nudge));
        return deps.delay({ ...updated });
      },
    },
  };
}
